import { localToInstant, toLocalDateTime } from './timezone.js';
/**
 * Repetição de compromissos. A regra é guardada como texto no formato do
 * padrão de calendário (RRULE da RFC 5545), num subconjunto pequeno:
 *
 * - `FREQ=DAILY|WEEKLY|MONTHLY|YEARLY` e `INTERVAL`
 * - `BYDAY=MO,WE,...` (só na semanal, sem posição como "2TU")
 * - `BYMONTHDAY=1..31` ou `-1` para o último dia do mês (só na mensal)
 * - `COUNT` ou `UNTIL` (nunca os dois)
 *
 * Como no padrão, dia que não existe é pulado: mensal no dia 31 não acontece
 * em mês de 30 dias, e anual em 29/02 só acontece em ano bissexto. Para "todo
 * fim de mês" existe `BYMONTHDAY=-1`.
 *
 * A série anda pela hora de parede do fuso dela: "toda segunda às 9h" continua
 * às 9h locais depois de uma virada de horário de verão.
 */
export const RECURRENCE_FREQUENCIES = ['DAILY', 'WEEKLY', 'MONTHLY', 'YEARLY'];
/** A posição é o dia da semana do JavaScript (0 = domingo). */
export const WEEKDAY_CODES = ['SU', 'MO', 'TU', 'WE', 'TH', 'FR', 'SA'];
export class RecurrenceError extends Error {
    code;
    constructor(code, message) {
        super(message);
        this.code = code;
        this.name = 'RecurrenceError';
    }
}
export const MAX_RECURRENCE_INTERVAL = 999;
export const MAX_RECURRENCE_COUNT = 9999;
/** Teto de ocorrências devolvidas por série numa expansão. */
export const DEFAULT_OCCURRENCE_LIMIT = 2000;
const DAY_MS = 24 * 60 * 60 * 1000;
/** Nenhuma série é calculada além deste ano. */
const LAST_YEAR = 2400;
const DATE_ONLY = /^\d{4}-\d{2}-\d{2}$/;
// ── Texto da regra ───────────────────────────────────────────────────────
function invalid(message) {
    throw new RecurrenceError('invalid_rrule', message);
}
function unsupported(message) {
    throw new RecurrenceError('unsupported_rrule', message);
}
function integerIn(text, min, max, label) {
    if (!/^-?\d+$/.test(text))
        invalid(`${label} precisa ser um número inteiro.`);
    const value = Number(text);
    if (value < min || value > max)
        invalid(`${label} fora do limite.`);
    return value;
}
function dayNumber(year, month, day) {
    return Math.round(Date.UTC(year, month, day) / DAY_MS);
}
function isRealDate(year, month, day) {
    const date = new Date(Date.UTC(year, month - 1, day));
    return (date.getUTCFullYear() === year && date.getUTCMonth() === month - 1 && date.getUTCDate() === day);
}
function parseUntil(text) {
    const match = /^(\d{4})(\d{2})(\d{2})(?:T(\d{2})(\d{2})(\d{2})Z)?$/.exec(text);
    if (!match)
        invalid('UNTIL precisa ser AAAAMMDD ou AAAAMMDDTHHMMSSZ.');
    const [, year, month, day, hour, minute, second] = match;
    if (!isRealDate(Number(year), Number(month), Number(day)))
        invalid('UNTIL tem uma data inválida.');
    if (hour === undefined)
        return `${year}-${month}-${day}`;
    if (Number(hour) > 23 || Number(minute) > 59 || Number(second) > 59) {
        invalid('UNTIL tem uma hora inválida.');
    }
    return `${year}-${month}-${day}T${hour}:${minute}:${second}.000Z`;
}
function checkUntil(until) {
    if (DATE_ONLY.test(until)) {
        const [year, month, day] = until.split('-').map(Number);
        if (!isRealDate(year, month, day))
            invalid('A data final é inválida.');
        return;
    }
    if (!/Z$/.test(until) || Number.isNaN(new Date(until).getTime())) {
        invalid('A data final precisa ser AAAA-MM-DD ou um instante em UTC.');
    }
}
/** Confere uma regra montada em código (a mesma régua do texto). */
export function assertValidRule(rule) {
    if (!RECURRENCE_FREQUENCIES.includes(rule.freq))
        invalid('Frequência desconhecida.');
    if (!Number.isInteger(rule.interval) ||
        rule.interval < 1 ||
        rule.interval > MAX_RECURRENCE_INTERVAL) {
        invalid('Intervalo fora do limite.');
    }
    if (rule.byDay !== undefined) {
        if (rule.freq !== 'WEEKLY')
            unsupported('Dias da semana só valem na repetição semanal.');
        if (rule.byDay.length === 0 || rule.byDay.some((code) => !WEEKDAY_CODES.includes(code))) {
            invalid('Dias da semana inválidos.');
        }
    }
    if (rule.byMonthDay !== undefined) {
        if (rule.freq !== 'MONTHLY')
            unsupported('Dias do mês só valem na repetição mensal.');
        if (rule.byMonthDay.length === 0 ||
            rule.byMonthDay.some((day) => !Number.isInteger(day) || (day !== -1 && (day < 1 || day > 31)))) {
            invalid('Dias do mês inválidos.');
        }
    }
    if (rule.count !== undefined && rule.until !== undefined) {
        invalid('Use contagem ou data final, não as duas.');
    }
    if (rule.count !== undefined &&
        (!Number.isInteger(rule.count) || rule.count < 1 || rule.count > MAX_RECURRENCE_COUNT)) {
        invalid('Contagem fora do limite.');
    }
    if (rule.until !== undefined)
        checkUntil(rule.until);
}
/** Lê o texto de uma regra ("FREQ=WEEKLY;BYDAY=MO,WE"). Lança `RecurrenceError`. */
export function parseRRule(text) {
    const body = text
        .trim()
        .toUpperCase()
        .replace(/^RRULE:/, '');
    if (!body)
        invalid('Regra de repetição vazia.');
    const parts = new Map();
    for (const piece of body.split(';')) {
        if (!piece)
            continue;
        const at = piece.indexOf('=');
        if (at <= 0 || at === piece.length - 1)
            invalid(`Parte inválida: ${piece}.`);
        const key = piece.slice(0, at);
        if (parts.has(key))
            invalid(`Parte repetida: ${key}.`);
        parts.set(key, piece.slice(at + 1));
    }
    const freq = parts.get('FREQ');
    if (!freq)
        invalid('Falta FREQ.');
    if (!RECURRENCE_FREQUENCIES.includes(freq)) {
        unsupported(`Frequência não suportada: ${freq}.`);
    }
    const rule = { freq: freq, interval: 1 };
    for (const [key, value] of parts) {
        switch (key) {
            case 'FREQ':
                break;
            case 'INTERVAL':
                rule.interval = integerIn(value, 1, MAX_RECURRENCE_INTERVAL, 'INTERVAL');
                break;
            case 'COUNT':
                rule.count = integerIn(value, 1, MAX_RECURRENCE_COUNT, 'COUNT');
                break;
            case 'UNTIL':
                rule.until = parseUntil(value);
                break;
            case 'BYDAY': {
                const codes = value.split(',');
                if (codes.some((code) => /^[+-]?\d/.test(code))) {
                    unsupported('Posição no mês ("2TU") não é suportada.');
                }
                if (codes.some((code) => !WEEKDAY_CODES.includes(code))) {
                    invalid('BYDAY tem um dia desconhecido.');
                }
                rule.byDay = [...new Set(codes)].sort((a, b) => mondayOffset(WEEKDAY_CODES.indexOf(a)) - mondayOffset(WEEKDAY_CODES.indexOf(b)));
                break;
            }
            case 'BYMONTHDAY': {
                const days = value.split(',').map((day) => integerIn(day, -1, 31, 'BYMONTHDAY'));
                if (days.includes(0))
                    invalid('BYMONTHDAY não aceita zero.');
                rule.byMonthDay = [...new Set(days)].sort((a, b) => monthDayOrder(a) - monthDayOrder(b));
                break;
            }
            case 'WKST':
                // A semana começa na segunda, como no padrão; outro começo não é suportado.
                if (value !== 'MO')
                    unsupported('Só semana começando na segunda (WKST=MO).');
                break;
            default:
                unsupported(`Parte não suportada: ${key}.`);
        }
    }
    assertValidRule(rule);
    return rule;
}
export function isValidRRule(text) {
    try {
        parseRRule(text);
        return true;
    }
    catch {
        return false;
    }
}
/** Texto canônico da regra, pronto para guardar. */
export function formatRRule(rule) {
    assertValidRule(rule);
    const parts = [`FREQ=${rule.freq}`];
    if (rule.interval !== 1)
        parts.push(`INTERVAL=${rule.interval}`);
    if (rule.byDay)
        parts.push(`BYDAY=${rule.byDay.join(',')}`);
    if (rule.byMonthDay)
        parts.push(`BYMONTHDAY=${rule.byMonthDay.join(',')}`);
    if (rule.count !== undefined)
        parts.push(`COUNT=${rule.count}`);
    if (rule.until !== undefined) {
        parts.push(`UNTIL=${DATE_ONLY.test(rule.until)
            ? rule.until.replaceAll('-', '')
            : new Date(rule.until)
                .toISOString()
                .replace(/\.\d{3}Z$/, 'Z')
                .replace(/[-:]/g, '')}`);
    }
    return parts.join(';');
}
function mondayOffset(weekday) {
    return (weekday + 6) % 7;
}
function monthDayOrder(day) {
    return day === -1 ? 32 : day;
}
function daysInMonth(year, month) {
    return new Date(Date.UTC(year, month + 1, 0)).getUTCDate();
}
function localDayNumber(local) {
    return dayNumber(local.year, local.month - 1, local.day);
}
/** Dias do calendário em que a regra cai, em ordem, a partir do dia inicial. */
function* candidateDays(rule, startDay) {
    const start = new Date(startDay * DAY_MS);
    const year0 = start.getUTCFullYear();
    const month0 = start.getUTCMonth();
    const day0 = start.getUTCDate();
    const lastDay = dayNumber(LAST_YEAR, 0, 1);
    if (rule.freq === 'DAILY') {
        for (let day = startDay; day < lastDay; day += rule.interval)
            yield day;
        return;
    }
    if (rule.freq === 'WEEKLY') {
        const weekday = start.getUTCDay();
        const offsets = [
            ...new Set((rule.byDay?.map((code) => WEEKDAY_CODES.indexOf(code)) ?? [weekday]).map(mondayOffset)),
        ].sort((a, b) => a - b);
        const weekStart = startDay - mondayOffset(weekday);
        for (let week = weekStart; week < lastDay; week += 7 * rule.interval) {
            for (const offset of offsets) {
                if (week + offset >= startDay)
                    yield week + offset;
            }
        }
        return;
    }
    if (rule.freq === 'MONTHLY') {
        for (let index = year0 * 12 + month0; index < LAST_YEAR * 12; index += rule.interval) {
            const year = Math.floor(index / 12);
            const month = index % 12;
            const last = daysInMonth(year, month);
            const days = [...new Set((rule.byMonthDay ?? [day0]).map((day) => (day === -1 ? last : day)))]
                .filter((day) => day <= last)
                .sort((a, b) => a - b);
            for (const day of days) {
                const candidate = dayNumber(year, month, day);
                if (candidate >= startDay)
                    yield candidate;
            }
        }
        return;
    }
    for (let year = year0; year < LAST_YEAR; year += rule.interval) {
        if (day0 <= daysInMonth(year, month0))
            yield dayNumber(year, month0, day0);
    }
}
function walkerOf(series) {
    assertValidRule(series.rule);
    const { rule, timeZone } = series;
    const startLocal = toLocalDateTime(series.start, timeZone);
    const startDay = localDayNumber(startLocal);
    const dayOf = (instant) => localDayNumber(toLocalDateTime(instant, timeZone));
    const instantOf = (day) => {
        if (day === startDay)
            return series.start;
        const date = new Date(day * DAY_MS);
        return localToInstant({
            year: date.getUTCFullYear(),
            month: date.getUTCMonth() + 1,
            day: date.getUTCDate(),
            hour: startLocal.hour,
            minute: startLocal.minute,
            second: startLocal.second,
        }, timeZone);
    };
    let untilDay = Number.POSITIVE_INFINITY;
    let untilMs = null;
    if (rule.until !== undefined) {
        if (DATE_ONLY.test(rule.until)) {
            const [year, month, day] = rule.until.split('-').map(Number);
            untilDay = dayNumber(year, month - 1, day);
        }
        else {
            const until = new Date(rule.until);
            untilMs = until.getTime();
            untilDay = dayOf(until);
        }
    }
    return {
        startDay,
        instantOf,
        dayOf,
        *days() {
            let index = 0;
            for (const day of candidateDays(rule, startDay)) {
                if (rule.count !== undefined && index >= rule.count)
                    return;
                if (day > untilDay)
                    return;
                if (day === untilDay && untilMs !== null && instantOf(day).getTime() > untilMs)
                    return;
                yield { index, day };
                index += 1;
            }
        },
    };
}
/** Fim de uma ocorrência que começa em `start`, pela duração da série. */
export function occurrenceEnd(series, start) {
    if (series.allDay) {
        const local = toLocalDateTime(start, series.timeZone);
        const end = new Date((localDayNumber(local) + Math.max(1, series.durationDays ?? 1)) * DAY_MS);
        return localToInstant({
            year: end.getUTCFullYear(),
            month: end.getUTCMonth() + 1,
            day: end.getUTCDate(),
            hour: 0,
            minute: 0,
            second: 0,
        }, series.timeZone);
    }
    return series.durationMs != null ? new Date(start.getTime() + series.durationMs) : null;
}
/**
 * Ocorrências da série dentro da janela `[from, to)`, já com as exceções
 * aplicadas: cancelada some, alterada aparece com o início e o fim próprios
 * (e conta pela hora nova, mesmo que a original esteja fora da janela).
 */
export function expandOccurrences(series, window, options = {}) {
    const { exceptions = [], limit = DEFAULT_OCCURRENCE_LIMIT, overlap = false } = options;
    const walker = walkerOf(series);
    const fromMs = window.from.getTime();
    const toMs = window.to.getTime();
    if (!(fromMs < toMs))
        return [];
    const byKey = new Map();
    const exceptionDays = new Set();
    let lastExceptionDay = Number.NEGATIVE_INFINITY;
    for (const exception of exceptions) {
        byKey.set(exception.occurrenceStart.getTime(), exception);
        const day = walker.dayOf(exception.occurrenceStart);
        exceptionDays.add(day);
        lastExceptionDay = Math.max(lastExceptionDay, day);
    }
    const span = series.allDay
        ? (Math.max(1, series.durationDays ?? 1) + 1) * DAY_MS
        : (series.durationMs ?? 0);
    // Folga de dois dias: o dia local e o dia em UTC nunca distam mais que isso.
    const lowDay = Math.floor((fromMs - (overlap ? span : 0)) / DAY_MS) - 2;
    const highDay = Math.ceil(toMs / DAY_MS) + 2;
    const found = [];
    for (const { index, day } of walker.days()) {
        if (day > highDay && day > lastExceptionDay)
            break;
        if ((day < lowDay || day > highDay) && !exceptionDays.has(day))
            continue;
        const originalStart = walker.instantOf(day);
        const exception = byKey.get(originalStart.getTime()) ?? null;
        if (exception?.cancelled)
            continue;
        const start = exception?.start ?? originalStart;
        const end = exception && exception.end !== undefined ? exception.end : occurrenceEnd(series, start);
        const startMs = start.getTime();
        if (startMs >= toMs)
            continue;
        if (startMs < fromMs && !(overlap && end !== null && end.getTime() > fromMs))
            continue;
        found.push({ index, originalStart, start, end, exception });
        if (found.length >= limit)
            break;
    }
    return found.sort((a, b) => a.start.getTime() - b.start.getTime() ||
        a.originalStart.getTime() - b.originalStart.getTime());
}
/** Posição da ocorrência que começa (pela regra) em `at`; `null` se não for da série. */
export function occurrenceIndex(series, at) {
    const walker = walkerOf(series);
    const target = walker.dayOf(at);
    for (const { index, day } of walker.days()) {
        if (day > target)
            return null;
        if (day === target)
            return walker.instantOf(day).getTime() === at.getTime() ? index : null;
    }
    return null;
}
/** A primeira data da série precisa cair na própria regra (ex.: começar numa terça se é "toda terça"). */
export function ruleMatchesStart(series) {
    const walker = walkerOf(series);
    const first = walker.days().next();
    return first.done !== true && first.value.day === walker.startDay;
}
/**
 * "Este e os próximos": corta a série na ocorrência que começa em `at`. O que
 * já passou fica como está (a regra antiga só ganha uma data final); a parte
 * nova herda o resto, com a contagem descontada.
 */
export function splitSeries(series, at) {
    const index = occurrenceIndex(series, at);
    if (index === null) {
        throw new RecurrenceError('not_an_occurrence', 'Essa data não é uma ocorrência da série.');
    }
    const { count, until, ...base } = series.rule;
    const after = { ...base };
    if (count !== undefined)
        after.count = count - index;
    if (until !== undefined)
        after.until = until;
    if (index === 0)
        return { index, before: null, after };
    return {
        index,
        before: { ...base, until: new Date(at.getTime() - 1000).toISOString() },
        after,
    };
}
